/*
    Form for creating or editing a queued message
*/

import React, { createRef } from "react";

import MainContext from "../MainContext";

import DateInput from "../Components/DateInput";
import TimeInput from "../Components/TimeInput";
import MultiSelect from "../Components/MultiSelect";
import StyledCheckbox from "../Components/StyledCheckbox";
import LocationDropDown from "../Components/LocationDropDown";

import StoredMessagesModal from "./StoredMessagesModal";

import { getLocations, getRoutes, getLanes } from "../Requests/UtilityRequests";

import { scrollToRef } from "../Scripts/Utilities";

const serviceTypes = [
    "TTS and Scrolling Text",
    "TTS Only",
    "Scrolling Text Only",
    "TTS, Scrolling, and Handheld",
    "Screen Blanking"
];

/*
    direction: "I"
    emissions: "1"
    enable: "true"
    enddate: "11/14/2018 12:00:00 AM"
    endtime: null
    frequency: "1234567"
    groupid: "NLR"
    interval: "3"
    lane: ""
    messageID: "6"
    phrase: "testing123"
    route: ""
    service_type: "1"
    startdate: "4/14/2016 2:45:00 PM"
    starttime: null
    terminal: "ALL"
*/

export default class MessageGeneralForm extends React.Component {

    static contextType = MainContext;

    constructor(){
        super();
        
        this.state = {
            messageId: null,
            serviceType: "TTS and Scrolling Text",
            location: "", // single selection for now
            locations: [],
            locationsSelected: [],
            routes: [],
            routesSelected: [],
            lanes: [],
            lanesSelected: [],
            ASAType: "",
            ASAData: null,
            startDate: new Date(),
            endDate: new Date(),
            startTime: this.getCurrentTimeString(),
            endTime: this.getCurrentTimeString(),
            daysOfWeek: "",
            minuteInterval: "",
            repetitionNumber: "",
            phrase: "",
            // inbound: true,
            enabled: true,
            // playNow: false,
            scheduled: false,
            selectAll: false
        };

        this.focusRef = createRef();
    }

    getCurrentTimeString() {
        const now = new Date();
        return `${now.getHours()}:${now.getMinutes()}`;
    }

    //
    // get base values to fill in the dropdown lists,
    // and fill in values if we're editing an existing record
    //
    componentDidMount(){
        // get Locations, Routes, Lanes
        // and fill in the selections if this is an edit form

        let request = () => getLocations();
        let then = ({data}) => {
            console.log("LOCATION LISTING", data);
            // const cleaned = data.map((l) => l.bus_terminal_code);
            this.setState({locations: data});
        };
        this.context.runProcessing(request, then, "getLocations");

        console.log("the user", this.context.user);

        this.setState({groupid: this.context.user.groupid});

        request = () => getRoutes();
        then = ({data}) => {
            const cleaned = data.map((r) => r.Route);
            this.setState({routes: cleaned});
        };
        this.context.runProcessing(request, then, "getRoutes");

        //
        // EDIT MODE
        //
        if(this.props.data){
            console.log("we're editing a record", this.props.data);

            const {data} = this.props;

            const locations = data.terminal
                ? data.terminal.split(",").filter(x => x).map(v => {
                    return {
                        bus_terminal_code: v,
                        bus_terminal_name: this.props.locationLookup[v]
                    }
                })
                : [data.terminal];

            // OK, so there's an issue with the selection here
            // because the MultiSelect component was designed for
            // checking direct reference matching
            // and this is all using value matching...
            // it would have been so much nicer if all of this had
            // been part of it from the beginning...

            console.log("MESSAGE LOCATIONS", locations);

            const routes = data.route 
                ? data.route.split(",").filter(x => x)
                : [data.route];

            const lanes = data.lane 
                ? data.lane.split(",").filter(x => x)
                : [data.lane];

            const now = new Date();

            const startdate = data.startdate
                ? new Date(data.startdate)
                : now;

            const enddate = data.enddate
                ? new Date(data.enddate)
                : now;

            const starttime = startdate
                ? startdate.getHours() + ":" + startdate.getMinutes()
                : this.getCurrentTimeString();

            const endtime = enddate
                ? enddate.getHours() + ":" + enddate.getMinutes()
                : this.getCurrentTimeString();

            const recordData = {
                messageId: data.messageID,
                serviceType: data.service_type,
                // location: data.terminal,
                isEdit: true, 
                locationsSelected: locations, 
                routesSelected: routes,
                lanesSelected: lanes,
                startDate: startdate,
                endDate: enddate,
                startTime: starttime,
                endTime: endtime,
                daysOfWeek: data.frequency,
                minuteInterval: data.interval,
                repetitionNumber: data.emissions,
                phrase: data.phrase,
                // inbound: data.direction === "I" ? true : false,
                enabled: data.enable === "true"
            };

            console.log("record message data", recordData);

            this.setState(recordData, () => {
                this.locationSelected(true);
            });
        }

        scrollToRef(this.focusRef.current);
    }

    //
    //
    updateValue = (evt) => {
        const name = evt.target.name;
        // console.log("UPDATE VALUE", evt.target.name, evt.target.value);
        this.setState({[evt.target.name]: evt.target.value}, () => {
            if(name === "locationsSelected"){
                this.locationSelected();
            }
        });
    }

    //
    //
    setPhrase = (val) => {
        this.setState({phrase: val});
    }

    //
    // when a location is selected, cascade to fill in routes and lanes
    //
    locationSelected = (leaveSelected) => {
        this.fillRoutes(leaveSelected);
        this.fillLanes(leaveSelected);
    }
    
    //
    // when route is selected, cascade to fill in lanes
    //
    routeSelected = () => {
        this.fillLanes();
    }

    //
    //
    fillRoutes = (leaveSelections) => {
        const locationString = this.state.locationsSelected.map(loc => loc.bus_terminal_code).join(",");
        const request = () => getRoutes(locationString);
        const then = ({data}) => {
            console.log("GENERAL MESSAGE GET ROUTES", data);
            const cleaned = data.map(v => v.Route);
            if(leaveSelections){
                this.setState({routes: cleaned});
            }
            else{
                this.setState({routes: cleaned, routesSelected: []});
            }
        };
        this.context.runProcessing(request, then, "getRoutes");
    }

    //
    //
    fillLanes = (leaveSelections) => {
        const request = () => getLanes({
            location: this.state.location, 
            route: this.state.routesSelected, 
            runId: this.state.runsSelected
        });
        const then = ({data}) => {
            const cleaned = data.map(v => v.laneGate);
            cleaned.unshift("None");
            if(leaveSelections){
                this.setState({lanes: cleaned});
            }
            else{
                this.setState({lanes: cleaned, lanesSelected: []});
            }
        };
        this.context.runProcessing(request, then, "getLanes");
    }

    //
    // the days field for stored messages represents the days of the week
    //
    checkDays = () => {
        // must only contain digits 1234567
        // must only contain each once (I'm not going to care about order for now)

        const seenSoFar = {};

        if(this.state.daysOfWeek.length > 7) {
            return false;
        }

        const chars = this.state.daysOfWeek.split("");

        for(let i = 0; i < chars.length; i++){
            const char = chars[i];
            console.log("CHAR", char);
            if("1234567".indexOf(char) >= 0){
                console.log("WE HAVE THE CHAR");
                if(seenSoFar[char]) {
                    return false;
                }
                seenSoFar[char] = true;
                console.log("SEEN SO FAR", seenSoFar);
            }
            else{
                return false;
            }
        }

        return true;
    }

    //
    //
    selectAllClick = () => {
        this.setState({selectAll: !this.state.selectAll}, () => {
            const all = this.state.selectAll;

            const newSelections = all ? this.state.locations : []; 

            console.log("SELECT ALL CLICK", all, newSelections);

            // select all of the locations
            // or deselect all of the locations

            this.setState({locationsSelected: newSelections}, () => {
                this.locationSelected();
            });

        });
    }

    //
    //
    submit = () => {
        let errorMessage = "";

        if(this.state.locationsSelected.length === 0 || this.state.phrase === ""){
            errorMessage = "Both location and phrase are required";
        }

        if(!this.checkDays()){
            errorMessage = (errorMessage ? errorMessage + "\n" : "") + "Invalid days format (digits must be within 1234567 without repeats)";
        }
        
        if(errorMessage){
            alert(errorMessage);
            return;
            // return;
        }

        console.log("message state", {...this.state});
        this.props.submit({...this.state, 
            locationsSelected: this.state.locationsSelected.map(v => v.bus_terminal_code)
        }, this.props.index);
    }

    //
    // make sure that the locations match based on terminal code
    //
    matchLocation = (v1, v2) => {
        return v1.bus_terminal_code === v2.bus_terminal_code;
    }

    render(){

        let buttonText = this.state.isEdit
                    ? "EDIT MESSAGE"
                    : this.state.minuteInterval ? "PLAY & SAVE NEW MESSAGE" : "PLAY NEW MESSAGE";

        return (
            <>
            {
                /*
                service type dropdown
                Route,
                Location,
                Lane,
                Begin Date,
                End Date,
                Valid Time Start,
                Valid Time End,
                Repetition Interval,
                Number of Repetitions,
                Days of Week,

                ASA Stuff TBD
                */
            }
                <div className="form-element-horizontal">
                    <label>Service Type</label>
                    <select ref={this.focusRef} name="serviceType" value={this.state.serviceType} onChange={this.updateValue}>
                    {
                        serviceTypes.map((type, index) => {
                            return <option key={index} value={type}>{type}</option>
                        })
                    }
                    </select>
                </div>

                <div className="form-element-horizontal" style={{position: "relative"}}>
                    <label>Location *</label>
                    {
                        /*
                        <LocationDropDown 
                            setFocus={true}
                            selected={this.state.location}
                            updateValue={this.updateValue}
                            hasAllOption={true}
                        />
                        */
                    }
                    <div style={{display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "2rem"}}>
                    <MultiSelect 
                        name="locationsSelected" 
                        selected={this.state.locationsSelected} 
                        options={this.state.locations} 
                        onChange={this.updateValue}
                        dataColumns={4} 
                        selectedDisplayColumns={1}
                        onDone={this.locationSelected}
                        displayField={"bus_terminal_name"}
                        selctionMatchFunction = {this.matchLocation}
                    />
                    {
                        this.context.user && this.context.user.groupid !== "BUS"
                        ? <div style={{alignSelf: "flex-start", marginTop: "0.5rem"}}>
                            <StyledCheckbox 
                                name="selectall" 
                                label="Select All" 
                                checked={this.state.selectAll} 
                                onChange={this.selectAllClick} 
                            />
                        </div>
                        : <></>
                    }
                    </div>
                </div>

                <div className="form-element-horizontal">
                    <label>Routes</label>
                    <MultiSelect 
                        name="routesSelected" 
                        selected={this.state.routesSelected} 
                        options={this.state.routes} 
                        onChange={this.updateValue} 
                        onDone={this.routeSelected}
                        dataColumns={8}
                    />
                </div>

                <div className="form-element-horizontal">
                    <label>Lanes</label>
                    <MultiSelect 
                        name="lanesSelected" 
                        selected={this.state.lanesSelected} 
                        options={this.state.lanes} 
                        dataColumns={8}
                        onChange={this.updateValue} />
                </div>

                <div id="message_form_enabled" style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gripGap: "4px"
                }}>
                    <StyledCheckbox 
                        name="enabled" 
                        label="Enabled" 
                        checked={this.state.enabled} 
                        onChange={() => this.setState({enabled: !this.state.enabled})} 
                    />
                    <StyledCheckbox 
                        name="scheduled" 
                        label="Scheduled" 
                        checked={this.state.scheduled} 
                        onChange={() => {
                            const now = new Date();

                            if(this.state.scheduled){
                                this.setState({
                                    scheduled: false,
                                    repetitionNumber: null,
                                    minuteInterval: null,
                                    daysOfWeek: ""
                                });
                            }
                            else{
                                this.setState({
                                    scheduled: true,
                                    repetitionNumber: 1,
                                    minuteInterval: 1,
                                    daysOfWeek: "1234567"
                                });
                            }

                            // no matter what
                            this.setState({
                                startDate: now,
                                endDate: now,
                                startTime: this.getCurrentTimeString(),
                                endTime: this.getCurrentTimeString()
                            });
                        }} 
                    />
                </div>

                {
                    this.state.scheduled
                    ? <>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr"
                        }}>
                            <div className="form-element-horizontal">
                                <label>Start Date</label>
                                <div>
                                    <DateInput name="startDate" value={new Date(this.state.startDate)} onChange={this.updateValue} />
                                    &nbsp;
                                    <TimeInput name="startTime" value={this.state.startTime} onChange={this.updateValue} />
                                </div>
                            </div>
                            
                            <div className="form-element-horizontal">
                                <label>End Date</label>
                                <div>
                                    <DateInput name="endDate" value={new Date(this.state.endDate)} onChange={this.updateValue} />
                                    &nbsp;
                                    <TimeInput name="endTime" value={this.state.endTime} onChange={this.updateValue} />
                                </div>
                            </div>
                        </div>

                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr"
                        }}>
                            <div className="form-element-horizontal">
                                <label># Repetitions</label>
                                <input className="short-input" type="number" name="repetitionNumber" value={this.state.repetitionNumber} onChange={this.updateValue}></input>
                            </div>

                            <div className="form-element-horizontal">
                                <label>Interval</label>
                                <input className="short-input" type="number" name="minuteInterval" value={this.state.minuteInterval} onChange={this.updateValue}></input>
                            </div>

                            <div className="form-element-horizontal">
                                <label>
                                    <div>
                                        Days of Week
                                        <br />
                                        (Mon = 1, Sun = 7)
                                    </div>
                                </label>
                                <input placeholder="ex: 12356" className="medium-input" type="number" name="daysOfWeek" value={this.state.daysOfWeek} onChange={this.updateValue}></input>
                            </div>
                        </div>
                    </>
                    : null
                }

                {
                    /*
                        text for the message, either user entered text
                        or one of several pre-made values
                    */
                }
                <div className="full-width-textarea-form-element">
                    <label>Phrase *</label>
                    <div style={{
                        display: "flex"
                    }}>
                        <textarea name="phrase" value={this.state.phrase} onChange={this.updateValue} style={{width:"100%"}}>
                        </textarea>
                        <StoredMessagesModal setPhrase={this.setPhrase} />
                    </div>
                </div>

                <div className="option vertical" onClick={this.submit}>{buttonText}</div>
            </>
        )
    }
}